/**
 * How the cost behind this page's figures was constructed.
 *
 * Quiet by design -- one grey line under the totals, with the rest behind a
 * disclosure. It is not a warning and must not read like one: every margin
 * here is internally consistent and reconciles to NOS. What it stops is the
 * other failure, where a 46.5% margin is carried into a conversation with a
 * competitor's fully-loaded number and nobody remembers that the construction
 * behind `employeecost` is still unconfirmed.
 *
 * `unknown` is the part worth expanding for: those are the questions this
 * system cannot answer from what it is granted, and they are the reason the
 * figure travels with its basis instead of alone.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { useId, useState } from "react";
import type { CostBasis } from "@/lib/profitability-api";

export function CostBasisNote({ basis }: { basis: CostBasis | undefined }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  if (!basis) return null;

  return (
    <div className="text-xs text-muted-foreground">
      <p>{basis.summary}</p>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="mt-1 inline-flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {open ? (
          <ChevronDown className="size-3" aria-hidden />
        ) : (
          <ChevronRight className="size-3" aria-hidden />
        )}
        How cost is built
        {basis.unknown.length > 0 ? ` · ${basis.unknown.length} open questions` : ""}
      </button>

      {open ? (
        <div id={panelId} className="mt-2 space-y-3 rounded-md border bg-muted/30 p-3">
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[10rem_1fr]">
            <dt className="font-medium text-foreground">Source</dt>
            <dd>{basis.source}</dd>
            <dt className="font-medium text-foreground">Method</dt>
            <dd>{basis.method}</dd>
            <dt className="font-medium text-foreground">Cost lines</dt>
            <dd>{basis.cost_line_types.join(", ")}</dd>
            <dt className="font-medium text-foreground">Revenue lines</dt>
            <dd>{basis.revenue_line_types.join(", ")}</dd>
            <dt className="font-medium text-foreground">Hourly rate applied</dt>
            {/* "No" is the interesting answer here: no division happens, which
                is why no hours denominator had to be chosen. */}
            <dd>{basis.hourly_rate_used ? (basis.denominator ?? "yes") : "no"}</dd>
          </dl>

          {basis.unknown.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">Not known from this data</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {basis.unknown.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p>{basis.consequence}</p>
          <p className="italic">{basis.to_resolve}</p>
        </div>
      ) : null}
    </div>
  );
}
